import { useAuth } from '@/core/auth/auth';
import type { Entitlements } from '@/core/auth/auth-state';

const DEFAULT_ENTITLEMENTS: Entitlements = {
  reports_limit: 15,
  can_use_all_templates: false,
  can_export_clean: false,
  custom_macros_limit: 50,
  daily_reports_used: 0,
  daily_reports_remaining: 15,
  grace_period_active: false,
  grace_period_ends_at: null,
};

/**
 * Hook to access entitlements from the bootstrap response.
 * Provides convenience methods for checking feature access.
 */
export function useEntitlements() {
  const { bootstrap } = useAuth();

  const entitlements: Entitlements = bootstrap?.entitlements ?? DEFAULT_ENTITLEMENTS;
  const isPaid = bootstrap?.account_status === 'active';
  const isFree = bootstrap?.account_status === 'free';

  /**
   * Check if the user can generate another report.
   * Paid users and grace-period users always can.
   * Free users are subject to daily limits.
   */
  const canGenerateReport = (): boolean => {
    if (isPaid || entitlements.grace_period_active) return true;
    if (entitlements.daily_reports_remaining === null) return true;
    return entitlements.daily_reports_remaining > 0;
  };

  /**
   * Get a display-friendly string for remaining reports.
   */
  const getReportsRemainingText = (): string => {
    if (isPaid || entitlements.grace_period_active) return 'Unlimited';
    if (entitlements.daily_reports_remaining === null) return 'Unlimited';
    return `${entitlements.daily_reports_remaining} remaining today`;
  };

  return {
    entitlements,
    isPaid,
    isFree,
    canGenerateReport,
    getReportsRemainingText,
    gracePeriodActive: entitlements.grace_period_active,
    gracePeriodEndsAt: entitlements.grace_period_ends_at,
  };
}
